import {
	Mail01,
	Folder,
	LogIn01,
	ShoppingCart01,
	Upload01,
	Plus,
	ClockRewind,
} from '#app/components/icons/solid'
import {
	Link,
	type Color,
	type ColorSpace,
	Button as AriaButton,
} from 'react-aria-components'
import { unstable_useRoute, useLocation, useNavigation } from 'react-router'
import Logo from '#app/components/logo.tsx'
import { TreeMenu } from '#app/components/tree-menu.tsx'
import { DOMAIN_CONFIGS, type DomainConfig } from '#app/config/domains.ts'
import { cn } from '#app/utils/misc.tsx'
import { usePreserveNuqsParams } from '#app/utils/navigation.ts'
import { type TreeNode } from '#app/utils/tree.server.ts'
import { useOptionalUser, useWorkspaceId } from '#app/utils/user.ts'
import {
	MobileNavigationHeader,
	NavAccountCard,
} from './application/app-navigation/sidebar-navigation-base'
import { NewIconSetModal } from './application/modals/new-icon-set-modal'
import { UpgradeModal } from './application/modals/upgrade-modal'
import { ButtonUtility } from './base/buttons/button-utility'
import { Button } from './shared/buttons/button'

type TreeNodeWithSvgLogo = TreeNode & { svgLogo?: string }

export const SidebarNavigationSectionsSubheadings = ({
	tree,
	uploadedTree,
	isLoadingUploaded,
	strokeWidth,
	setStrokeWidth,
	color,
	setColor,
	colorSpace,
	setColorSpace,
}: {
	tree: TreeNodeWithSvgLogo[]
	uploadedTree: TreeNode[] | null | undefined
	isLoadingUploaded?: boolean
	strokeWidth: string
	setStrokeWidth: (strokeWidth: string) => void
	color: Color
	setColor: (color: Color) => void
	colorSpace: ColorSpace | 'hex'
	setColorSpace: (colorSpace: ColorSpace | 'hex') => void
}) => {
	const MAIN_SIDEBAR_WIDTH = 292

	return (
		<>
			{/* Mobile header navigation */}
			<MobileNavigationHeader
				strokeWidth={strokeWidth}
				setStrokeWidth={setStrokeWidth}
				color={color}
				setColor={setColor}
				colorSpace={colorSpace}
				setColorSpace={setColorSpace}
			>
				<SidebarContent
					tree={tree}
					uploadedTree={uploadedTree}
					isLoadingUploaded={isLoadingUploaded}
				/>
			</MobileNavigationHeader>

			{/* Desktop sidebar navigation */}
			<div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:py-1 lg:pl-1">
				<SidebarContent
					tree={tree}
					uploadedTree={uploadedTree}
					isLoadingUploaded={isLoadingUploaded}
				/>
			</div>

			{/* Placeholder to take up physical space because the real sidebar has `fixed` position. */}
			<div
				style={{
					paddingLeft: MAIN_SIDEBAR_WIDTH + 4,
				}}
				className="invisible hidden lg:sticky lg:top-0 lg:bottom-0 lg:left-0 lg:block"
			/>
		</>
	)
}

const SidebarContent = ({
	tree,
	uploadedTree,
	isLoadingUploaded,
}: {
	tree: TreeNodeWithSvgLogo[]
	uploadedTree: TreeNode[] | null | undefined
	isLoadingUploaded?: boolean
}) => {
	const MAIN_SIDEBAR_WIDTH = 292
	const buildUrl = usePreserveNuqsParams(['redirectTo'])
	const location = useLocation()
	const navigation = useNavigation()
	const rootData = unstable_useRoute('root')?.loaderData
	const domainConfig = rootData?.domainConfig || DOMAIN_CONFIGS.localhost
	const user = useOptionalUser()
	const workspaceId = useWorkspaceId()

	return (
		<aside
			style={
				{
					'--width': `${MAIN_SIDEBAR_WIDTH}px`,
				} as React.CSSProperties
			}
			className="border-secondary bg-primary flex h-full w-full max-w-full flex-col justify-between overflow-auto pt-3 shadow-xs [scrollbar-color:--alpha(var(--color-fg-secondary)/20%)_transparent] [scrollbar-width:thin] md:border-r lg:w-(--width) lg:rounded-xl lg:border lg:pt-5"
		>
			<div className="flex flex-col gap-5 px-4 lg:px-5">
				<Link
					href={buildUrl('/')}
					className="group bg-primary outline-focus-ring hover:bg-primary_hover relative flex w-full cursor-pointer items-center rounded-md px-3 py-2 transition duration-100 ease-linear select-none focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2"
				>
					<Logo
						variant={domainConfig.logoType}
						className="text-fg-quaternary transition-inherit-all mr-3 size-6 shrink-0"
					/>
					<span className="text-md text-secondary transition-inherit-all group-hover:text-secondary_hover flex-1 font-semibold">
						{domainConfig.name}
					</span>
				</Link>
			</div>

			{/* Recently Used */}
			<RecentlyUsedLink domainConfig={domainConfig} />

			<div>
				<>
					<div className="flex items-center justify-between px-5 pb-2.5">
						<p className="text-quaternary text-xs font-bold uppercase">
							Uploaded icon sets
						</p>
						{user && (
							<NewIconSetModal
								trigger={
									<ButtonUtility
										icon={Plus}
										size="xs"
										color="tertiary"
										tooltip="New icon set"
									/>
								}
							/>
						)}
					</div>

					<div className="px-5 pb-5">
						<div className="flex flex-col gap-1">
							{user && isLoadingUploaded && (
								<div className="text-tertiary flex items-center gap-2 px-3 py-2 text-sm">
									<div className="border-tertiary size-4 animate-spin rounded-full border-2 border-t-transparent" />
									Loading...
								</div>
							)}
							{user && !isLoadingUploaded ? (
								uploadedTree && uploadedTree.length > 0 ? (
									<>
										<Link
											href={buildUrl(
												`/workspace/${workspaceId}/icon-sets`,
											)}
											className={(() => {
												const allUploadedPath = `/workspace/${workspaceId}/icon-sets`
												const isActive =
													!navigation.location &&
													location.pathname === allUploadedPath

												const isPending =
													navigation.location &&
													navigation.location.pathname === allUploadedPath

												return `outline-focus-ring hover:bg-primary_hover relative flex w-full cursor-pointer items-center gap-3 rounded-md py-2 pr-3 pl-3 transition duration-100 ease-linear select-none focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2 ${
													isActive || isPending
														? 'bg-primary_hover'
														: 'bg-primary'
												}`
											})()}
										>
											<div className="relative size-5 shrink-0">
												<Folder className="text-fg-quaternary transition-inherit-all size-5" />
											</div>
											All
										</Link>
										<TreeMenu items={uploadedTree} isEditable />
									</>
								) : (
									<NewIconSetModal
										trigger={
											<Button iconLeading={Upload01}>Upload an Icon Set</Button>
										}
									/>
								)
							) : null}

							{!user && (
								<UpgradeModal
									trigger={
										<Button iconLeading={Upload01}>Upload an Icon Set</Button>
									}
									domainConfig={domainConfig}
								/>
							)}
						</div>
					</div>
				</>

				<div className="px-5 pb-2.5">
					<p className="text-quaternary text-xs font-bold uppercase">
						Free icon sets
					</p>
				</div>

				<div className="px-5 pb-5">
					<div className="flex flex-col gap-1">
						<Link
							href={buildUrl('/')}
							className={(() => {
								const isActive =
									!navigation.location && location.pathname === '/'

								const isPending =
									navigation.location && navigation.location.pathname === '/'

								return `outline-focus-ring hover:bg-primary_hover relative flex w-full cursor-pointer items-center gap-3 rounded-md py-2 pr-3 pl-3 transition duration-100 ease-linear select-none focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2 ${
									isActive || isPending ? 'bg-primary_hover' : 'bg-primary'
								}`
							})()}
						>
							<div className="relative size-5 shrink-0">
								<Folder className="text-fg-quaternary transition-inherit-all size-5" />
							</div>
							All
						</Link>

						<TreeMenu items={tree} />
					</div>
				</div>
			</div>

			<div className="mt-auto flex flex-col gap-4 px-2 py-4 lg:px-4 lg:py-6">
				<div className="flex flex-col gap-1">
					<SidebarLink
						href={`mailto:${domainConfig.supportEmail}`}
						icon={Mail01}
					>
						Support or Feedback
					</SidebarLink>
					{!user && (
						<div className="flex items-center gap-2 pt-2">
							<Button href={buildUrl('/login')} iconLeading={LogIn01}>
								Login
							</Button>
							<span className="text-secondary text-sm font-semibold">or</span>
							<UpgradeModal
								trigger={
									<Button iconLeading={ShoppingCart01}>Unlock Pro</Button>
								}
								domainConfig={domainConfig}
							/>
						</div>
					)}
				</div>
				{user && <NavAccountCard email={user.email} name={user.name} />}
			</div>
		</aside>
	)
}

// Reusable sidebar link component
const SidebarLink = ({
	href,
	icon: Icon,
	children,
	isActive = false,
	isPending = false,
}: {
	href: string
	icon: React.ComponentType<{ className?: string }>
	children: React.ReactNode
	isActive?: boolean
	isPending?: boolean
}) => {
	return (
		<Link
			href={href}
			className={cn(
				'outline-focus-ring hover:bg-primary_hover relative flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2 transition duration-100 ease-linear select-none focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2',
				isActive || isPending ? 'bg-primary_hover' : 'bg-primary',
			)}
		>
			<Icon className="text-fg-quaternary transition-inherit-all size-6 shrink-0" />
			<span className="flex-1">{children}</span>
		</Link>
	)
}

const RecentlyUsedLink = ({ domainConfig }: { domainConfig: DomainConfig }) => {
	const buildUrl = usePreserveNuqsParams(['redirectTo'])
	const user = useOptionalUser()
	const workspaceId = useWorkspaceId()
	const navigation = useNavigation()
	const location = useLocation()

	if (!user) {
		return (
			<div className="mt-8 px-5 pb-2.5">
				<UpgradeModal
					trigger={
						<AriaButton
							className={cn(
								'outline-focus-ring hover:bg-primary_hover relative flex w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-left transition duration-100 ease-linear select-none focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2',
							)}
						>
							<ClockRewind className="text-fg-quaternary transition-inherit-all size-6 shrink-0" />
							<span className="flex-1">Recently Used</span>
						</AriaButton>
					}
					domainConfig={domainConfig}
				/>
			</div>
		)
	}

	const recentlyUsedPath = `/workspace/${workspaceId}/recently-used`
	const isActive =
		!navigation.location && location.pathname === recentlyUsedPath
	const isPending = Boolean(
		navigation.location && navigation.location.pathname === recentlyUsedPath,
	)

	return (
		<div className="mt-8 px-5 pb-2.5">
			<SidebarLink
				href={buildUrl(recentlyUsedPath)}
				icon={ClockRewind}
				isActive={isActive}
				isPending={isPending}
			>
				Recently Used
			</SidebarLink>
		</div>
	)
}
